import { QuoteExcludedNote, SummaryCard } from './styles';
import { AddOnsLine, TotalLine } from './summary-lines';

import React from 'react';
import { formatCurrency } from 'utils/formatCurrency';
import { useCurrencyType } from 'hooks/use-currency-type.hook';
import { useIntl } from 'react-intl';
import { useLocale } from 'hooks/use-locale.hook';

interface EstimateSummaryProps {
  /* Whether anything is chosen — what separates "nothing yet" from "cannot be stated". */
  hasSelection: boolean;
  /* Minor units. The client's own sum of catalog unit prices. */
  estimateTotal: number;
  /* The currency the total is in, or null when the selection spans more than one — or lacks one. */
  estimateCurrency: string | null;
  /*
   * A line carries no price yet, so the figure above covers only part of the order. Gated on this
   * alone, never on `estimateCurrency`: a quote-only selection has no priced line and therefore no
   * currency, which is exactly the case the sentence exists for.
   */
  hasUnpriceable?: boolean;
}

/*
 * Stands in for an amount that cannot be STATED — a selection spanning currencies. ⚠️ Not for an empty
 * selection, which is a real zero. An em dash (U+2014).
 */
export const WITHHELD_AMOUNT = '—';

/*
 * The money on this step, as the design's summary card: what the add-ons come to, and the subtotal.
 *
 * The figure is the client's own sum of catalog unit prices: price times quantity, nothing else. It is
 * not the charge, because this sum never includes tax for any party, and billing's own sheet states the
 * real total at the next step. That is why the row says Subtotal rather than Total. It says "Estimated
 * total" instead wherever no figure can be stated at all.
 *
 * Amounts are withheld when the currency is unknown, exactly as the CTA beneath does. `AddOn.currency`
 * is optional, so one USD item beside one carrying none makes a single figure meaningless. One party
 * per booking does not close this: the grey-out is per party, not per currency. The rule lives in the
 * hook; this honours it rather than re-deriving it.
 */
interface SummaryAmountInput {
  hasSelection: boolean;
  estimateTotal: number;
  estimateCurrency: string | null;
  buildingCurrency: string;
  locale: string;
}

/*
 * The figure the card shows, as a pure function so the three states can be tested without rendering
 * -- the same shape `ctaMessage` uses.
 *
 * Nothing selected is a real zero in the building's currency, NOT a withheld amount: the hook reports
 * `estimateCurrency: null` there too, but only because there are no lines to read a currency from.
 * The dash is reserved for an amount that genuinely cannot be stated -- mixed currencies, or a
 * quote-required product.
 */
export const summaryAmount = ({
  hasSelection,
  estimateTotal,
  estimateCurrency,
  buildingCurrency,
  locale,
}: SummaryAmountInput): string => {
  if (!hasSelection) {
    return formatCurrency(0, buildingCurrency, locale, true, false);
  }

  if (!estimateCurrency) {
    return WITHHELD_AMOUNT;
  }

  return formatCurrency(estimateTotal, estimateCurrency, locale, true, false);
};

export const EstimateSummary = ({
  hasSelection,
  estimateTotal,
  estimateCurrency,
  hasUnpriceable = false,
}: EstimateSummaryProps): JSX.Element => {
  const intl = useIntl();
  const locale = useLocale();
  const buildingCurrency = useCurrencyType();

  const amount = summaryAmount({ hasSelection, estimateTotal, estimateCurrency, buildingCurrency, locale });
  /*
   * The hedge belongs to a figure that cannot be stated: a quote-required line, or a selection spanning
   * currencies. A wholly priced basket in one currency has a firm subtotal and says so, and so does an
   * empty one -- its zero is a real figure in the building's currency, not a withheld amount.
   */
  const statesSubtotal = !hasSelection || Boolean(estimateCurrency && !hasUnpriceable);

  return (
    <SummaryCard data-testid="booking-add-ons-summary">
      <AddOnsLine title={intl.formatMessage({ id: 'add_ons' })} price={amount} />
      <TotalLine
        title={intl.formatMessage({ id: statesSubtotal ? 'subtotal' : 'add_on_total_estimated' })}
        price={amount}
      />
      {hasUnpriceable && (
        <QuoteExcludedNote data-testid="booking-add-ons-quote-excluded">
          {intl.formatMessage({ id: 'add_on_quote_excluded' })}
        </QuoteExcludedNote>
      )}
    </SummaryCard>
  );
};
